import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { CheckIcon } from '@arcade/web/icons/react'

type OptionVariant = 'inline' | 'stack' | 'action'

const OptionWrapper = styled.div<{ disabled?: boolean; variant: OptionVariant }>`
  align-items: center;
  display: flex;
  padding: ${({ variant }) => (variant === 'stack' || variant === 'action' ? '0.75rem 1rem' : '0')};
  overflow: hidden;
  white-space: nowrap;

  ${({ theme, variant }) =>
    (variant === 'stack' || variant === 'action') &&
    css`
      &:active {
        background: ${theme.colors.rowBackgroundActive};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.25;
    `}
`

const OptionProfilePic = styled.div`
  margin-right: 0.75em;
`

const OptionLabelContainer = styled.div<{ variant: OptionVariant }>`
  display: ${({ variant }) => (variant === 'stack' || variant === 'action' ? 'block' : 'flex')};
  min-width: 0;
`

const OptionTitle = styled.div<{ variant: OptionVariant; hasLogo?: boolean }>`
  color: ${({ theme }) => theme.colors.label};
  ${({ theme }) => theme.typography.input};
  ${({ variant, theme }) =>
    variant === 'stack' &&
    css`
      padding-bottom: ${theme.spacing.xxs};
    `}
  ${({ hasLogo, variant }) =>
    !hasLogo &&
    variant === 'inline' &&
    css`
      margin-left: 0.375rem;
    `}
`

const OptionSubtitle = styled.div`
  color: ${({ theme }) => theme.colors.tertiaryLabel};
`

const OptionSubtitleInline = styled.span`
  color: ${({ theme }) => theme.colors.tertiaryLabel};
  ${({ theme }) => theme.typography.input};
  margin-left: ${({ theme }) => theme.spacing.s};
  flex: 1 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const OptionInfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  align-items: center;
  width: 80%;
`

export const DropdownOption = ({
  disabled,
  logo,
  title,
  subtitle,
  variant,
  name = '',
  isSelected = false,
}: {
  disabled?: boolean
  logo?: React.ReactElement
  title: React.ReactNode
  subtitle?: string
  variant: OptionVariant
  name?: string
  isSelected?: boolean
}) => {
  return (
    <OptionWrapper
      data-testid={`${name}-${title}-${variant}`}
      disabled={disabled}
      variant={variant}
      onClick={e => {
        if (disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    >
      {logo && <OptionProfilePic>{logo}</OptionProfilePic>}
      <OptionInfoWrapper>
        <OptionLabelContainer variant={variant}>
          <OptionTitle variant={variant} hasLogo={!!logo}>
            {title}
          </OptionTitle>
          {subtitle &&
            (variant === 'stack' ? (
              <OptionSubtitle>{subtitle}</OptionSubtitle>
            ) : (
              <OptionSubtitleInline>{subtitle}</OptionSubtitleInline>
            ))}
        </OptionLabelContainer>
        {isSelected && <CheckIcon data-testid="selected-option-icon" />}
      </OptionInfoWrapper>
    </OptionWrapper>
  )
}
